import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowUp, ArrowLeft, ExternalLink, Loader2, Send } from "lucide-react";

export default function PostDetail() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");

  const load = useCallback(() => {
    api.get(`/posts/${id}`).then((r) => setPost(r.data));
    api.get(`/posts/${id}/comments`).then((r) => setComments(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const upvote = async () => { if (!user) { login(); return; } await api.post(`/posts/${id}/upvote`); load(); };
  const comment = async (e) => {
    e.preventDefault();
    if (!user) { login(); return; }
    if (!text.trim()) return;
    await api.post(`/posts/${id}/comments`, { text });
    setText(""); load();
  };

  if (!post) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/community" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-cyan-300 mb-6"><ArrowLeft className="w-4 h-4" />Back to community</Link>

      <div className="glass rounded-2xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <button onClick={upvote} className="flex flex-col items-center gap-1 shrink-0 px-3 py-2 rounded-lg border border-white/10 hover:border-cyan-500/40" data-testid="post-detail-upvote">
            <ArrowUp className={`w-5 h-5 ${post.upvoted_by?.includes(user?.user_id) ? "text-cyan-400" : "text-slate-500"}`} />
            <span className="font-mono text-slate-300">{post.upvotes}</span>
          </button>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold text-slate-50">{post.title}</h1>
            <p className="text-slate-300 mt-2">{post.description}</p>
            {post.problem && <p className="text-sm text-slate-400 mt-3"><span className="text-slate-500">Problem: </span>{post.problem}</p>}
            <div className="flex items-center gap-4 mt-4 text-sm text-slate-500 flex-wrap">
              <span className="flex items-center gap-1.5"><Avatar className="w-6 h-6"><AvatarImage src={post.author_picture} /><AvatarFallback className="text-[9px] bg-cyan-500/20 text-cyan-300">{post.author_name?.[0]}</AvatarFallback></Avatar>{post.author_name}</span>
              {post.tech_stack && <span className="font-mono text-xs">{post.tech_stack}</span>}
              {post.pricing && <span className="font-mono text-xs text-emerald-400">{post.pricing}</span>}
              {post.url && <a href={post.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-cyan-400">Visit site <ExternalLink className="w-3.5 h-3.5" /></a>}
            </div>
          </div>
        </div>
      </div>

      <h3 className="font-semibold text-slate-200 mb-4">{comments.length} Comments</h3>
      <form onSubmit={comment} className="flex gap-2 mb-6">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={user ? "Share feedback…" : "Log in to comment"} className="bg-white/5 border-white/10 text-slate-200 min-h-11" data-testid="comment-input" />
        <Button type="submit" className="bg-cyan-500 text-slate-950 self-end" data-testid="comment-submit"><Send className="w-4 h-4" /></Button>
      </form>
      <div className="space-y-3">
        {comments.map((c) => (
          <div key={c.id} className="glass rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1.5"><Avatar className="w-6 h-6"><AvatarImage src={c.author_picture} /><AvatarFallback className="text-[9px] bg-cyan-500/20 text-cyan-300">{c.author_name?.[0]}</AvatarFallback></Avatar><span className="text-sm text-slate-300">{c.author_name}</span></div>
            <p className="text-sm text-slate-400">{c.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
